import React, { useRef } from 'react';
import { PriceAlert } from '../types';
import { getTimeFrameName } from '../utils/timeFrameUtils';
import { DateFormat } from '@/services/enums';

interface PriceAlertItemProps {
  alert: PriceAlert;
  onClick?: () => void;
  onLongPress?: () => void;
  isWatched?: boolean;
  className?: string;
  timeFormat?: DateFormat;
}

const PriceAlertItem: React.FC<PriceAlertItemProps> = ({
  alert,
  onClick,
  onLongPress,
  isWatched = false,
  className = '',
  timeFormat = 'short',
}) => {
  // 长按/点击冲突防抖控制
  const longPressTriggeredRef = useRef(false);
  const longPressTimerRef = useRef<number | null>(null);

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const startLongPressTimer = (_evt: React.TouchEvent | React.MouseEvent) => {
    clearLongPressTimer();
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true;
      onLongPress?.();
    }, 500);
  };

  // 触摸事件处理
  const handleTouchStart = (e: React.TouchEvent) => {
    longPressTriggeredRef.current = false;
    startLongPressTimer(e);
  };

  const handleTouchMove = () => {
    // 移动时取消长按
    clearLongPressTimer();
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    clearLongPressTimer();
    // 若已触发长按，则阻止后续 click
    if (longPressTriggeredRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // 鼠标事件处理（用于桌面端测试）
  const handleMouseDown = (e: React.MouseEvent) => {
    longPressTriggeredRef.current = false;
    startLongPressTimer(e);
    const handleMouseUp = () => {
      clearLongPressTimer();
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (longPressTriggeredRef.current) {
      // 已长按触发，屏蔽点击跳转
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick?.();
  };
  // 格式化时间
  const formatTime = (timestamp: string) => {
    if (timeFormat === 'detailed') {
      return new Date(timestamp).toLocaleString('zh-CN', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } else if (timeFormat === 'history') {
      return new Date(timestamp).toLocaleString('zh-CN', {
        timeZone: 'Asia/Shanghai',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } else {
      return new Date(timestamp).toLocaleString('zh-CN', {
        timeZone: 'Asia/Shanghai',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  // 获取容器样式
  const getContainerStyles = () => {
    const baseStyles = 'relative h-16 cursor-pointer group border-t-2 border-b-2 rounded-xl';

    if (alert.alertType === 'NEW_COIN') {
      return `${baseStyles} border-yellow-400/50`;
    } else if (alert.alertType === 'NEW_HIGH') {
      return `${baseStyles} border-purple-400/50`;
    } else if (alert.alertType === 'NEW_LOW') {
      return `${baseStyles} border-orange-400/50`;
    } else if (alert.alertType === 'FUNDING_RATE_HIGH') {
      return `${baseStyles} border-blue-400/50`;
    } else if (alert.alertType === 'POSITION_PROFIT') {
      return `${baseStyles} border-green-400/50`;
    } else if (alert.alertType === 'POSITION_LOSS') {
      return `${baseStyles} border-red-400/50`;
    } else if (alert.alertType === 'POSITION_CHANGE') {
      return `${baseStyles} border-yellow-400/50`;
    } else if (alert.alertType === 'BREAK_RESISTANCE') {
      return `${baseStyles} border-cyan-400/50`;
    } else if (alert.alertType === 'BREAK_SUPPORT') {
      return `${baseStyles} border-pink-400/50`;
    } else if (alert.alertType === 'VOLUME_SURGE') {
      return `${baseStyles} border-indigo-400/50`;
    } else if (alert.isNewCoin && alert.alertType === 'SURGE') {
      return `${baseStyles} border-green-400/50`;
    } else if (alert.isNewCoin && alert.alertType === 'DROP') {
      return `${baseStyles} border-red-400/50`;
    } else if (alert.alertType === 'SURGE') {
      return `${baseStyles} border-green-400/50`;
    } else {
      return `${baseStyles} border-red-400/50`;
    }
  };

  return (
    <div
      className={`${getContainerStyles()} ${className}`}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      style={{ minHeight: '64px' }}
    >
      <div className="w-full min-h-16 flex items-center justify-between px-4 py-2 relative overflow-visible">
        {/* 币对名称 - 占满左侧空白区域 */}
        <div className="flex-1 min-w-0 mr-3 sm:mr-4 overflow-hidden">
          <div
            className={`font-semibold text-sm truncate flex items-center ${
              alert.isNewCoin && alert.alertType !== 'NEW_COIN' ? 'text-yellow-200' : 'text-white'
            }`}
          >
            {/* 如果是新币种的涨跌告警，在名称前添加特殊标识 */}
            {alert.isNewCoin && alert.alertType !== 'NEW_COIN' && (
              <span className="mr-1 text-yellow-400 flex-shrink-0">✨</span>
            )}
            <span className="truncate">{alert.symbol}</span>
            {/* 关注状态指示器 */}
            {isWatched && <span className="ml-1 sm:ml-2 text-blue-400 text-xs flex-shrink-0">👁️</span>}
          </div>
        </div>

        {/* 右侧固定宽度元素：时间粒度、变化率、日期 */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {/* 时间粒度标签 - 固定宽度 */}
          <div className="text-white/70 text-xs bg-white/10 px-1.5 sm:px-2 py-1 rounded-full whitespace-nowrap w-12 sm:w-14 text-center">
            <span className="truncate block">{getTimeFrameName(alert.timeFrame)}</span>
          </div>

          {/* 价格变化 - 固定宽度 */}
          <div className="flex-shrink-0 w-14 sm:w-20 text-right">
            {alert.alertType === 'NEW_COIN' ? (
              <div className="font-bold text-sm sm:text-base text-yellow-300 flex items-center justify-end whitespace-nowrap">
                <span className="mr-1">🆕</span>
              </div>
            ) : alert.alertType === 'NEW_HIGH' ? (
              <div className="font-bold text-sm sm:text-base text-purple-300 flex items-center justify-end whitespace-nowrap">
                <span className="mr-1 hidden sm:inline">📈</span>
                {Number(alert.priceChangePercent) > 0 ? '+' : ''}
                {Number(alert.priceChangePercent).toFixed(2)}%
              </div>
            ) : alert.alertType === 'NEW_LOW' ? (
              <div className="font-bold text-sm sm:text-base text-orange-300 flex items-center justify-end whitespace-nowrap">
                <span className="mr-1 hidden sm:inline">📉</span>
                {Number(alert.priceChangePercent) > 0 ? '+' : ''}
                {Number(alert.priceChangePercent).toFixed(2)}%
              </div>
            ) : alert.alertType === 'FUNDING_RATE_HIGH' ? (
              <div className="font-bold text-sm sm:text-base text-blue-300 flex items-center justify-end whitespace-nowrap">
                <span className="mr-1 hidden sm:inline">💰</span>
                {Number(alert.priceChangePercent) > 0 ? '+' : ''}
                {Number(alert.priceChangePercent).toFixed(4)}%
              </div>
            ) : alert.alertType === 'POSITION_PROFIT' ? (
              <div className="font-bold text-sm sm:text-base text-green-300 flex items-center justify-end whitespace-nowrap">
                <span className="mr-1 hidden sm:inline">💵</span>
                {Number(alert.priceChangePercent) > 0 ? '+' : ''}
                {Number(alert.priceChangePercent).toFixed(2)}%
              </div>
            ) : alert.alertType === 'POSITION_LOSS' ? (
              <div className="font-bold text-sm sm:text-base text-red-300 flex items-center justify-end whitespace-nowrap">
                <span className="mr-1 hidden sm:inline">💸</span>
                {Number(alert.priceChangePercent) > 0 ? '+' : ''}
                {Number(alert.priceChangePercent).toFixed(2)}%
              </div>
            ) : alert.alertType === 'POSITION_CHANGE' ? (
              <div className="font-bold text-sm sm:text-base text-yellow-300 flex items-center justify-end whitespace-nowrap">
                <span className="mr-1 hidden sm:inline">📊</span>
                <span>变化</span>
              </div>
            ) : alert.alertType === 'BREAK_RESISTANCE' ? (
              <div className="font-bold text-sm sm:text-base text-cyan-300 flex items-center justify-end whitespace-nowrap">
                <span className="mr-1 hidden sm:inline">🚀</span>
                {Number(alert.priceChangePercent) > 0 ? '+' : ''}
                {Number(alert.priceChangePercent).toFixed(2)}%
              </div>
            ) : alert.alertType === 'BREAK_SUPPORT' ? (
              <div className="font-bold text-sm sm:text-base text-pink-300 flex items-center justify-end whitespace-nowrap">
                <span className="mr-1 hidden sm:inline">📉</span>
                {Number(alert.priceChangePercent) > 0 ? '+' : ''}
                {Number(alert.priceChangePercent).toFixed(2)}%
              </div>
            ) : alert.alertType === 'VOLUME_SURGE' ? (
              <div className="font-bold text-sm sm:text-base text-indigo-300 flex items-center justify-end whitespace-nowrap">
                <span className="mr-1 hidden sm:inline">📊</span>
                {Number(alert.priceChangePercent) > 0 ? '+' : ''}
                {Number(alert.priceChangePercent).toFixed(2)}%
              </div>
            ) : (
              <div
                className={`font-bold text-sm sm:text-base flex items-center justify-end whitespace-nowrap ${
                  alert.alertType === 'SURGE' ? 'text-green-300' : 'text-red-300'
                }`}
              >
                {Number(alert.priceChangePercent) > 0 ? '+' : ''}
                {Number(alert.priceChangePercent).toFixed(2)}%
              </div>
            )}
          </div>

          {/* 时间戳 - 固定宽度 */}
          <div className="text-white/60 text-xs whitespace-nowrap hidden sm:block w-32 text-right">
            {formatTime(alert.timestamp)}
          </div>
          {/* 移动端显示简化时间戳 - 固定宽度 */}
          <div className="text-white/60 text-xs whitespace-nowrap sm:hidden w-16 text-right">
            {new Date(alert.timestamp).toLocaleString('zh-CN', {
              timeZone: 'Asia/Shanghai',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceAlertItem;
